/**
 * Dates, times, money and phone numbers the way docs/52 §7.3 says: digits, "Thursday, Oct 9", "2:14 pm" in the
 * business's time zone. Pure functions, unit tested in format.test.ts.
 */

const DEFAULT_TZ = 'America/Chicago';

function parts(iso: string | Date, tz: string, opts: Intl.DateTimeFormatOptions): Record<string, string> {
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  const out: Record<string, string> = {};
  for (const p of new Intl.DateTimeFormat('en-US', { timeZone: tz || DEFAULT_TZ, ...opts }).formatToParts(d)) out[p.type] = p.value;
  return out;
}

/** "2:14 pm" */
export function timeLabel(iso: string | Date, tz: string): string {
  const p = parts(iso, tz, { hour: 'numeric', minute: '2-digit', hour12: true });
  return `${p.hour}:${p.minute} ${(p.dayPeriod ?? '').toLowerCase()}`;
}

/** "Thursday, Oct 9" */
export function dateLabel(iso: string | Date, tz: string): string {
  const p = parts(iso, tz, { weekday: 'long', month: 'short', day: 'numeric' });
  return `${p.weekday}, ${p.month} ${p.day}`;
}

/** "Oct 9" */
export function shortDate(iso: string | Date, tz: string): string {
  const p = parts(iso, tz, { month: 'short', day: 'numeric' });
  return `${p.month} ${p.day}`;
}

/** "Oct 9, 2026" */
export function longDate(iso: string | Date, tz: string): string {
  const p = parts(iso, tz, { month: 'short', day: 'numeric', year: 'numeric' });
  return `${p.month} ${p.day}, ${p.year}`;
}

/** A calendar day (YYYY-MM-DD) has no zone: read it at noon UTC so it never slips a day. */
export function dayLabel(day: string, withYear = false): string {
  const d = new Date(`${day}T12:00:00.000Z`);
  return withYear ? longDate(d, 'UTC') : dateLabel(d, 'UTC');
}
export function dayShort(day: string): string {
  return shortDate(new Date(`${day}T12:00:00.000Z`), 'UTC');
}

/** "October 2026" from YYYY-MM */
export function monthLabel(month: string): string {
  const d = new Date(`${month}-15T12:00:00.000Z`);
  const p = parts(d, 'UTC', { month: 'long', year: 'numeric' });
  return `${p.month} ${p.year}`;
}

/**
 * A report's name (deck ruling c136): the period it covers when it has one ("Sep 5 to Oct 3, 2026"), else its month
 * for a report keyed to a month, else the scan day it's keyed to.
 */
export function reportLabel(key: string, period?: { label: string } | null): string {
  if (period?.label) return period.label;
  if (/^\d{4}-\d{2}$/.test(key)) return monthLabel(key);
  return /^\d{4}-\d{2}-\d{2}$/.test(key) ? dayLabel(key, true) : key;
}

function ymd(iso: string | Date, tz: string): string {
  const p = parts(iso, tz, { year: 'numeric', month: '2-digit', day: '2-digit' });
  return `${p.year}-${p.month}-${p.day}`;
}

/** Row time: "2:14 pm" today, "Yesterday", the weekday this week, else "Oct 3". */
export function rowTime(iso: string, tz: string, now: Date = new Date()): string {
  const day = ymd(iso, tz);
  if (day === ymd(now, tz)) return timeLabel(iso, tz);
  const yesterday = new Date(now.getTime() - 86_400_000);
  if (day === ymd(yesterday, tz)) return 'Yesterday';
  const age = now.getTime() - new Date(iso).getTime();
  if (age > 0 && age < 6 * 86_400_000) return parts(iso, tz, { weekday: 'long' }).weekday ?? shortDate(iso, tz);
  return shortDate(iso, tz);
}

/** "just now", "5 minutes ago", "2 hours ago", "3 days ago", then the date. */
export function ago(iso: string, tz: string, now: Date = new Date()): string {
  const s = Math.max(0, Math.round((now.getTime() - new Date(iso).getTime()) / 1000));
  if (s < 60) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return m === 1 ? '1 minute ago' : `${m} minutes ago`;
  const h = Math.round(m / 60);
  if (h < 24) return h === 1 ? '1 hour ago' : `${h} hours ago`;
  const d = Math.round(h / 24);
  if (d < 7) return d === 1 ? 'yesterday' : `${d} days ago`;
  return `on ${shortDate(iso, tz)}`;
}

/** "$497" or "$497.50". Cents in, never floats on the wire. */
export function money(cents: number): string {
  const neg = cents < 0;
  const abs = Math.abs(cents);
  const whole = Math.floor(abs / 100).toLocaleString('en-US');
  const rest = abs % 100;
  return `${neg ? '−' : ''}$${whole}${rest ? `.${String(rest).padStart(2, '0')}` : ''}`;
}

/** +14805550142 → (480) 555-0142 */
export function phoneLabel(e164: string): string {
  const m = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(e164);
  return m ? `(${m[1]}) ${m[2]}-${m[3]}` : e164;
}

/** "Good morning" by the business's clock. */
export function partOfDay(tz: string, now: Date = new Date()): 'morning' | 'afternoon' | 'evening' {
  const h = Number(parts(now, tz, { hour: 'numeric', hour12: false }).hour);
  return h < 12 ? 'morning' : h < 17 ? 'afternoon' : 'evening';
}

/** 08:00 → "8 am", 14:30 → "2:30 pm", 24:00 → "midnight". */
export function clockLabel(hhmm: string): string {
  if (hhmm === '24:00' || hhmm === '00:00') return 'midnight';
  const [h, m] = hhmm.split(':').map(Number) as [number, number];
  const period = h < 12 ? 'am' : 'pm';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return m ? `${h12}:${String(m).padStart(2, '0')} ${period}` : `${h12} ${period}`;
}

export function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

export function bytesLabel(n: number): string {
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
